import {
  ChangeDetectionStrategy,
  Component,
  ViewContainerRef,
  OnDestroy,
  ComponentRef,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  OnInit,
  Renderer2,
  Inject,
  AfterContentInit,
  TemplateRef,
  ContentChild,
} from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig, ConnectionPositionPair } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { MdbDatepickerContentComponent } from './datepicker-content.component';
import { filter, take, takeUntil } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import {
  addMonths,
  addYears,
  createDate,
  getDate,
  getDayNumber,
  getDaysInMonth,
  getMonth,
  getYear,
  getYearsOffset,
  isDateDisabled,
  isMonthDisabled,
  isNextDateDisabled,
  isPreviousDateDisabled,
  isValidDate,
  isYearDisabled,
  isFullYearFormat,
} from './datepicker-utils';
import { MdbDatepickerToggleComponent } from './datepicker-toggle.component';
import { DOCUMENT } from '@angular/common';
import { YEARS_IN_VIEW } from './datepicker-const';
import { MdbDatepickerInputDirective } from './datepicker-input.directive';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { MDB_DATEPICKER_HEADER } from './datepicker-header.directive';
import { MdbDatepickerOptions } from './datepicker.interface';

export type MdbDatepickerView = 'days' | 'months' | 'years';

@Component({
  selector: 'mdb-datepicker',
  exportAs: 'mdbDatepicker',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MdbDatepickerComponent implements OnInit, AfterContentInit, OnDestroy {
  @ContentChild(MDB_DATEPICKER_HEADER, { read: TemplateRef, static: true })
  _headerRef: TemplateRef<any>;

  @Input()
  get confirmDateOnSelect(): boolean {
    return this._confirmDateOnSelect;
  }
  set confirmDateOnSelect(value: boolean) {
    this._confirmDateOnSelect = coerceBooleanProperty(value);
  }
  private _confirmDateOnSelect = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }
  private _disabled = false;

  @Input()
  get inline(): boolean {
    return this._inline;
  }
  set inline(value: boolean) {
    this._inline = coerceBooleanProperty(value);
  }
  private _inline = false;

  @Input()
  get removeCancelBtn(): boolean {
    return this._removeCancelBtn;
  }
  set removeCancelBtn(value: boolean) {
    this._removeCancelBtn = coerceBooleanProperty(value);
  }
  private _removeCancelBtn = false;

  @Input()
  get removeClearBtn(): boolean {
    return this._removeClearBtn;
  }
  set removeClearBtn(value: boolean) {
    this._removeClearBtn = coerceBooleanProperty(value);
  }
  private _removeClearBtn = false;

  @Input()
  get removeOkBtn(): boolean {
    return this._removeOkBtn;
  }
  set removeOkBtn(value: boolean) {
    this._removeOkBtn = coerceBooleanProperty(value);
  }
  private _removeOkBtn = false;

  @Input() format = 'dd/mm/yyyy';
  @Input() filter: (date: Date) => boolean;

  @Input()
  get openOnInputClick(): boolean {
    return this._openOnInputClick;
  }
  set openOnInputClick(value: boolean) {
    this._openOnInputClick = coerceBooleanProperty(value);
  }
  private _openOnInputClick = false;

  get headerRef(): TemplatePortal | null {
    return this._headerPortal;
  }
  private _headerPortal: TemplatePortal | null = null;

  @Input()
  get options(): MdbDatepickerOptions {
    return this._options;
  }
  set options(value: MdbDatepickerOptions) {
    this._options = {
      ...this._options,
      ...value,
    };
  }
  private _options: MdbDatepickerOptions = {
    cancelBtnLabel: 'Cancel selection',
    cancelBtnText: 'Cancel',
    clearBtnLabel: 'Clear selection',
    clearBtnText: 'Clear',
    monthsFull: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthsShort: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    nextMonthLabel: 'Next month',
    nextMultiYearLabel: 'Next 24 years',
    nextYearLabel: 'Next year',
    okBtnLabel: 'Confirm selection',
    okBtnText: 'Ok',
    prevMonthLabel: 'Previous month',
    prevMultiYearLabel: 'Previous 24 years',
    prevYearLabel: 'Previous year',
    switchToDayViewLabel: 'Choose date',
    switchToMultiYearViewLabel: 'Choose year and month',
    title: 'Select date',
    weekdaysFull: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    weekdaysNarrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  };

  @Input() startDate: null | Date;
  @Input() startDay = 0;
  @Input() startView: MdbDatepickerView = 'days';
  @Input() minDate: Date;
  @Input() maxDate: Date;

  @Output() readonly dateChanged = new EventEmitter<Date>();
  @Output() readonly viewChanged = new EventEmitter<MdbDatepickerView>();
  @Output() readonly opened = new EventEmitter<void>();
  @Output() readonly closed = new EventEmitter<void>();

  private _overlayRef: OverlayRef | null;
  private _portal: ComponentPortal<MdbDatepickerContentComponent>;
  private _pickerRef: ComponentRef<MdbDatepickerContentComponent>;

  _activeDate = new Date();
  _selectedDate: null | Date;
  _selectedYear: null | number;
  _selectedMonth: null | number;

  _prevBtnDisabled = false;
  _nextBtnDisabled = false;

  _currentView: MdbDatepickerView;

  _input: HTMLInputElement;
  _inputDirective: MdbDatepickerInputDirective;
  _toggle: MdbDatepickerToggleComponent;
  _headerDate: string;

  _isOpen = false;

  get activeDay(): number {
    return getDate(this._activeDate);
  }

  get activeMonth(): number {
    return getMonth(this._activeDate);
  }

  get activeYear(): number {
    return getYear(this._activeDate);
  }

  get firstYearInView(): number {
    return (
      this.activeYear - getYearsOffset(this._activeDate, YEARS_IN_VIEW, this.minDate, this.maxDate)
    );
  }

  get lastYearInView(): number {
    return this.firstYearInView + YEARS_IN_VIEW - 1;
  }

  get viewChangeButtonText(): string {
    if (this._currentView === 'days') {
      return `${this.options.monthsFull?.[this.activeMonth] ?? ''} ${this.activeYear}`;
    }

    if (this._currentView === 'years') {
      return `${this.firstYearInView} - ${this.lastYearInView}`;
    }

    if (this._currentView === 'months') {
      return `${this.activeYear}`;
    }
    return '';
  }

  get viewChangeButtonLabel(): string {
    if (this._currentView === 'days') {
      return this.options.switchToMultiYearViewLabel ?? '';
    } else if (this._currentView === 'years' || this._currentView === 'months') {
      return this.options.switchToDayViewLabel ?? '';
    }
    return '';
  }

  get nextButtonLabel(): string {
    if (this._currentView === 'days') {
      return this.options.nextMonthLabel ?? '';
    } else if (this._currentView === 'years') {
      return this.options.nextMultiYearLabel ?? '';
    } else {
      return this.options.nextYearLabel ?? '';
    }
  }

  get prevButtonLabel(): string {
    if (this._currentView === 'days') {
      return this.options.prevMonthLabel ?? '';
    } else if (this._currentView === 'years') {
      return this.options.prevMultiYearLabel ?? '';
    } else {
      return this.options.prevYearLabel ?? '';
    }
  }

  constructor(
    @Inject(DOCUMENT) private _document,
    private _overlay: Overlay,
    private _vcr: ViewContainerRef,
    private _renderer: Renderer2,
    private _cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._inputDirective.selectionChange.subscribe((date) => {
      this._applyInputDate(date);
    });
    const headerDate = this._selectedDate || this._activeDate;
    this._updateHeaderDate(headerDate);
  }

  ngAfterContentInit(): void {
    if (this._headerRef) {
      this._createHeaderPortal();
    }
  }

  _updateHeaderDate(date: Date): void {
    const day = getDayNumber(date);
    const dayNumber = getDate(date);
    const month = getMonth(date);
    const options = this.options;

    this._headerDate = `${options.weekdaysShort?.[day] ?? ''}, ${options.monthsShort?.[month] ?? ''} ${dayNumber}`;
  }

  open(): void {
    if (this._isOpen || this.disabled) {
      return;
    }

    this._currentView = this.startView;
    this._setInitialDate();

    let overlayRef = this._overlayRef;

    if (!overlayRef) {
      this._portal = new ComponentPortal<MdbDatepickerContentComponent>(
        MdbDatepickerContentComponent,
        this._vcr
      );
      overlayRef = this._overlay.create(this._getOverlayConfig());
      this._overlayRef = overlayRef;
    }

    if (overlayRef && !overlayRef.hasAttached()) {
      this._pickerRef = overlayRef.attach(this._portal);
      this._pickerRef.instance.datepicker = this;

      this._listenToOutsideClick();
    }

    if (!this.inline && this._hasVerticalScroll()) {
      const widthWithVerticalScroll = this._document.body.offsetWidth;

      this._renderer.setStyle(this._document.body, 'overflow', 'hidden');

      const widthWithoutVerticalScroll = this._document.body.offsetWidth;

      this._renderer.setStyle(
        this._document.body,
        'padding-right',
        `${widthWithoutVerticalScroll - widthWithVerticalScroll}px`
      );
    }

    this._isOpen = true;
    this.opened.emit();
    this._cdRef.markForCheck();
  }

  private _getOverlayConfig(): OverlayConfig {
    const inlinePositionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this._input)
      .withPositions(this._getPositions())
      .withFlexibleDimensions(false);

    const dialogPositionStrategy = this._overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const inlineScrollStrategy = this._overlay.scrollStrategies.reposition();
    const dialogScrollStrategy = this._overlay.scrollStrategies.noop();

    const positionStrategy = this.inline ? inlinePositionStrategy : dialogPositionStrategy;
    const scrollStrategy = this.inline ? inlineScrollStrategy : dialogScrollStrategy;

    const overlayConfig = new OverlayConfig({
      hasBackdrop: this.inline ? false : true,
      backdropClass: 'mdb-backdrop',
      scrollStrategy,
      positionStrategy,
    });
    return overlayConfig;
  }

  private _getPositions(): ConnectionPositionPair[] {
    return [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
      },
    ];
  }

  private _hasVerticalScroll(): boolean {
    return this._document.body.scrollHeight > this._document.documentElement.clientHeight;
  }

  private _listenToOutsideClick(): void {
    if (!this._overlayRef) {
      return;
    }

    merge(
      this._overlayRef.keydownEvents().pipe(
        filter((event) => {
          return event.key === 'Escape';
        })
      ),
      fromEvent<MouseEvent>(document, 'click').pipe(
        takeUntil(this.closed),
        filter((event: MouseEvent) => {
          const toggleButton = this._toggle ? this._toggle.button.nativeElement : null;
          const target = event.target as HTMLElement;
          const notToggle = toggleButton
            ? target !== toggleButton && !toggleButton.contains(target)
            : true;
          const notOrigin = target !== this._input;
          const notOverlay =
            !!this._overlayRef &&
            this._overlayRef.overlayElement &&
            !this._overlayRef.overlayElement.contains(target);
          return notToggle && notOrigin && notOverlay;
        })
      )
    ).subscribe(() => {
      this.close();
    });
  }

  close(): void {
    if (!this._isOpen || !this._overlayRef) {
      return;
    }

    this._pickerRef.instance._startHideAnimation();
    if (this._overlayRef.backdropElement) {
      this._renderer.setStyle(this._overlayRef.backdropElement, 'opacity', '0');
    }

    this._pickerRef.instance._hideAnimationDone.pipe(take(1)).subscribe(() => {
      if (this._overlayRef && this._overlayRef.hasAttached()) {
        this._overlayRef.dispose();
        this._overlayRef = null;
        this._isOpen = false;
      }
      if (!this.inline) {
        this._renderer.removeStyle(this._document.body, 'overflow');
        this._renderer.removeStyle(this._document.body, 'padding-right');
      }

      this.closed.emit();

      if (this._toggle) {
        this._toggle.button.nativeElement.focus();
      } else {
        this._input.focus();
      }
    });

    this._cdRef.markForCheck();
  }

  toggle(): void {
    this._isOpen ? this.close() : this.open();
  }

  viewChange(): void {
    this._setView(this._currentView === 'days' ? 'years' : 'days');
    this._updateControlsDisabledState();
  }

  _handlePreviousButtonClick(): void {
    if (this._currentView === 'days') {
      this._previousMonth();
    } else if (this._currentView === 'years') {
      this._previousYears();
    } else {
      this._previousYear();
    }

    this._updateControlsDisabledState();
  }

  _handleNextButtonClick(): void {
    if (this._currentView === 'days') {
      this._nextMonth();
    } else if (this._currentView === 'years') {
      this._nextYears();
    } else {
      this._nextYear();
    }

    this._updateControlsDisabledState();
  }

  _updateControlsDisabledState(): void {
    if (
      isNextDateDisabled(
        this._activeDate,
        this._currentView,
        YEARS_IN_VIEW,
        this.minDate,
        this.maxDate
      )
    ) {
      this._nextBtnDisabled = true;
    } else {
      this._nextBtnDisabled = false;
    }

    if (
      isPreviousDateDisabled(
        this._activeDate,
        this._currentView,
        YEARS_IN_VIEW,
        this.minDate,
        this.maxDate
      )
    ) {
      this._prevBtnDisabled = true;
    } else {
      this._prevBtnDisabled = false;
    }
  }

  _nextMonth(): void {
    this._activeDate = addMonths(this._activeDate, 1);
  }

  _previousMonth(): void {
    this._activeDate = addMonths(this._activeDate, -1);
  }

  _nextYear(): void {
    this._activeDate = addYears(this._activeDate, 1);
  }

  _previousYear(): void {
    this._activeDate = addYears(this._activeDate, -1);
  }

  _nextYears(): void {
    this._activeDate = addYears(this._activeDate, 24);
  }

  _previousYears(): void {
    this._activeDate = addYears(this._activeDate, -24);
  }

  _handleUserInput(input: string, emitChanges = true): void {
    const delimeters = this._getDelimeters(this.format);
    const date = this._parseDate(input, this.format, delimeters);

    if (isValidDate(date) && !isDateDisabled(date, this.minDate, this.maxDate, this.filter)) {
      this._activeDate = date;
      this._selectedDate = date;
    } else {
      this._activeDate = new Date();
      this._selectedDate = null;
      this._selectedMonth = null;
      this._selectedYear = null;
    }

    if (emitChanges) {
      const valueToEmit = isValidDate(date) ? date : null;
      this._inputDirective.onChange(valueToEmit);
    }
  }

  _getDelimeters(format: string): any {
    return format.match(/[^(dmy)]{1,}/g);
  }

  _parseDate(dateString: string, format: string, delimeters: any): Date {
    if (!dateString) {
      return new Date(NaN);
    }

    let delimeterPattern: string;

    if (delimeters[0] !== delimeters[1]) {
      delimeterPattern = delimeters[0] + delimeters[1];
    } else {
      delimeterPattern = delimeters[0];
    }

    const regExp = new RegExp(`[${delimeterPattern}]`);
    const dateParts = dateString.split(regExp);
    const formatParts = format.split(regExp);
    const isMonthString = format.indexOf('mmm') !== -1;

    const datesArray: Array<{ value: string; format: string }> = [];

    for (let i = 0; i < formatParts.length; i++) {
      if (formatParts[i].indexOf('yy') !== -1) {
        datesArray[0] = { value: dateParts[i], format: formatParts[i] };
      }
      if (formatParts[i].indexOf('m') !== -1) {
        datesArray[1] = { value: dateParts[i], format: formatParts[i] };
      }
      if (formatParts[i].indexOf('d') !== -1 && formatParts[i].length <= 2) {
        datesArray[2] = { value: dateParts[i], format: formatParts[i] };
      }
    }

    let monthsNames: string[];

    if (format.indexOf('mmmm') !== -1) {
      monthsNames = this.options.monthsFull ?? [];
    } else {
      monthsNames = this.options.monthsShort ?? [];
    }

    let year = Number(datesArray[0].value);

    if (!isFullYearFormat(datesArray[0].value) && year < 100) {
      year += year >= 50 ? 1900 : 2000;
    }

    const month = isMonthString
      ? this._getMonthNumberByMonthName(datesArray[1].value, monthsNames)
      : Number(datesArray[1].value) - 1;
    const day = Number(datesArray[2].value);

    const parsedDate = createDate(year, month, day);

    if (month > 11 || month < 0 || day > getDaysInMonth(createDate(year, month, 1)) || day < 1) {
      return new Date(NaN);
    }

    return parsedDate;
  }

  private _getMonthNumberByMonthName(monthValue: string, monthLabels: string[]): number {
    return monthLabels.findIndex((monthLabel: string) => monthLabel === monthValue);
  }

  _applyInputDate(date: Date | null): void {
    if (!date) {
      this._clearDate();
      return;
    }

    if (isDateDisabled(date, this.minDate, this.maxDate, this.filter)) {
      return;
    }

    this._selectedDate = date;
    this._activeDate = date;
    this._updateHeaderDate(date);

    const dateString = this._formatDate(date);
    this._input.value = dateString;
    this._renderer.addClass(this._input, 'active');

    if (this._pickerRef) {
      this._pickerRef.instance.detectChanges();
    }

    this.dateChanged.emit(this._selectedDate);
  }

  _selectDate(date: Date): void {
    if (isDateDisabled(date, this.minDate, this.maxDate, this.filter)) {
      return;
    }

    this._selectedDate = date;
    this._activeDate = date;
    this._updateHeaderDate(date);

    if (this.inline || this._confirmDateOnSelect) {
      this._confirmSelection(date);
      this.close();
    }
  }

  _confirmSelection(date: Date): void {
    if (date) {
      const dateString = this._formatDate(date);
      this._input.value = dateString;
      this._renderer.addClass(this._input, 'active');
      this._inputDirective.onChange(date);
      this.dateChanged.emit(date);
    }
  }

  _formatDate(date: Date): string {
    const d = getDate(date);
    const dd = this._addLeadingZero(getDate(date));
    const ddd = this.options.weekdaysShort?.[getDayNumber(date)] ?? '';
    const dddd = this.options.weekdaysFull?.[getDayNumber(date)] ?? '';
    const m = getMonth(date) + 1;
    const mm = this._addLeadingZero(getMonth(date) + 1);
    const mmm = this.options.monthsShort?.[getMonth(date)] ?? '';
    const mmmm = this.options.monthsFull?.[getMonth(date)] ?? '';
    const yy =
      getYear(date).toString().length === 2 ? getYear(date) : getYear(date).toString().slice(2, 4);
    const yyyy = getYear(date);

    const preformatted = this.format.split(/(d{1,4}|m{1,4}|y{4}|yy|!.)/g);
    let formatted = '';
    preformatted.forEach((datePart) => {
      switch (datePart) {
        case 'dddd':
          datePart = datePart.replace(datePart, dddd.toString());
          break;
        case 'ddd':
          datePart = datePart.replace(datePart, ddd.toString());
          break;
        case 'dd':
          datePart = datePart.replace(datePart, dd.toString());
          break;
        case 'd':
          datePart = datePart.replace(datePart, d.toString());
          break;
        case 'mmmm':
          datePart = datePart.replace(datePart, mmmm.toString());
          break;
        case 'mmm':
          datePart = datePart.replace(datePart, mmm.toString());
          break;
        case 'mm':
          datePart = datePart.replace(datePart, mm.toString());
          break;
        case 'm':
          datePart = datePart.replace(datePart, m.toString());
          break;
        case 'yyyy':
          datePart = datePart.replace(datePart, yyyy.toString());
          break;
        case 'yy':
          datePart = datePart.replace(datePart, yy.toString());
          break;
        default:
      }
      formatted += datePart;
    });

    return formatted;
  }

  private _addLeadingZero(value: any): string | number {
    return parseInt(value, 10) < 10 ? `0${value}` : value;
  }

  _selectYear(year: number): void {
    if (isYearDisabled(year, this.minDate, this.maxDate)) {
      return;
    }

    const daysInMonth = getDaysInMonth(createDate(year, this.activeMonth, 1));
    const day = Math.min(this.activeDay, daysInMonth);

    const newDate = createDate(year, this.activeMonth, day);

    this._activeDate = newDate;
    this._selectedYear = year;
    this._setView('months');

    this._updateHeaderDate(newDate);
    this._updateControlsDisabledState();
  }

  _selectMonth(month: number): void {
    const year = this.activeYear;

    if (
      isMonthDisabled(month, year, this.minDate, this.maxDate) ||
      isYearDisabled(year, this.minDate, this.maxDate)
    ) {
      return;
    }

    const daysInMonth = getDaysInMonth(createDate(year, month, 1));
    const day = Math.min(this.activeDay, daysInMonth);

    const newDate = createDate(year, month, day);

    this._activeDate = newDate;
    this._selectedMonth = month;
    this._setView('days');

    this._updateHeaderDate(newDate);
    this._updateControlsDisabledState();
  }

  _handleOkClick(): void {
    if (this._selectedDate) {
      this._confirmSelection(this._selectedDate);
    }
    this.close();
  }

  _handleCancelClick(): void {
    this._selectedDate = null;
    this._selectedMonth = null;
    this._selectedYear = null;
    this.close();
  }

  _handleClearClick(): void {
    this._clearDate();
    this._inputDirective.onChange(null);
  }

  private _clearDate(): void {
    this._selectedDate = null;
    this._selectedMonth = null;
    this._selectedYear = null;
    this._input.value = '';
    this._renderer.removeClass(this._input, 'active');
    this._setInitialDate();
    this._updateHeaderDate(this._activeDate);
    this._setView('days');
  }

  private _setView(view: MdbDatepickerView): void {
    this._currentView = view;
    this.viewChanged.emit(this._currentView);
  }

  private _setInitialDate(): void {
    if (this._input.value) {
      this._handleUserInput(this._input.value, false);
    } else if (this.startDate) {
      this._activeDate = this.startDate;
    } else {
      this._activeDate = new Date();
    }

    this._updateHeaderDate(this._activeDate);
    this._updateControlsDisabledState();
  }

  private _createHeaderPortal(): void {
    this._headerPortal = new TemplatePortal(this._headerRef, this._vcr);
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this.close();
      this._overlayRef.dispose();
    }
  }

  static ngAcceptInputType_confirmDateOnSelect: BooleanInput;
  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_inline: BooleanInput;
  static ngAcceptInputType_openOnInputClick: BooleanInput;
  static ngAcceptInputType_removeCancelBtn: BooleanInput;
  static ngAcceptInputType_removeClearBtn: BooleanInput;
  static ngAcceptInputType_removeOkBtn: BooleanInput;
}
